import {moment} from 'moment';
import * as ko from "knockout";
import {ErrorManager} from "../../../error/ErrorManager";
import {ISyncField, PartialRecord, TSyncfield} from "../../../index";
import {ErrorObject} from "../../../error/ErrorObject";
import {nsUserWizard} from "./nsUserWizard";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {getRawSyncFieldValue} from "../../../util/misc";
import {convertToServerString} from "../../../util/datetime";
import {INPUT_DATE_FORMAT} from "../../../constants";
import {TSyncfield_Department} from "../../../constants/Syncfields";
import {IModelParams, TOperationType} from "./index";
import {AbstractModel} from "./AbstractModel";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {global} from "../../../appContext";
import TWizardSyncfield = nsUserWizard.TWizardSyncfield;
import TObjectData_basic = nsSyncFields.TObjectData_basic;
import TPositionType = nsUserWizard.TPositionType;

interface IMeta {
    [key: string]: KnockoutObservable<any>

    department: KnockoutObservable<TObjectData_basic | null>
    end: KnockoutObservable<string>
    isNoEndDate: KnockoutObservable<boolean | null>
    isNoStartDate: KnockoutObservable<boolean | null>
    start: KnockoutObservable<string>
}

export interface IOptionalSyncfieldObject {
    syncfield: ISyncField
    values: KnockoutObservable<any>[]
}

export interface IPositionModelParams extends IModelParams {
    positionType: TPositionType
    optionalSyncfields: KnockoutObservableArray<ISyncField>
    onAddOptionalSyncfieldClick: (syncfield: ISyncField) => void
    onRemoveOptionalSyncfieldClick: (syncfield: ISyncField) => void
    opertationType: TOperationType
}

export class PositionModel extends AbstractModel {

    public override readonly attr: PartialRecord<TWizardSyncfield, KnockoutObservable<any>> = {
        Title: ko.observable(null),
        Company: ko.observable(global.login.selectedOrganization()?.organizationame),
    }
    public override readonly errorManager: ErrorManager = new ErrorManager()
    public override readonly meta: IMeta = {
        department: ko.observable(null),
        end: ko.observable(''),
        isNoEndDate: ko.observable(null),
        isNoStartDate: ko.observable(null),
        start: ko.observable(''),
    }

    private readonly eo: Record<'DepartmentNumber' | 'ExpiryDate' | 'Start' | 'Title', ErrorObject> = {
        DepartmentNumber: new ErrorObject({name: 'DepartmentNumber'}),
        ExpiryDate: new ErrorObject({name: 'ExpiryDate'}),
        Start: new ErrorObject({name: 'Start'}),
        Title: new ErrorObject({name: 'Title'}),
    }

    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker()
    public readonly positionType: TPositionType
    public readonly optionalSyncfields: KnockoutObservableArray<ISyncField>;
    public readonly optionalSyncfieldsSearchKey: KnockoutObservable<string | null> = ko.observable(null)
    public readonly optionalSyncfieldObservables: Partial<Record<TSyncfield, KnockoutObservable<any>>> = {}
    public readonly onAddOptionalSyncfieldClick: (syncfield: ISyncField) => void
    public readonly onRemoveOptionalSyncfieldClick: (syncfield: ISyncField) => void
    private readonly operationType: TOperationType;

    constructor(params: IPositionModelParams) {
        super(params);
        this.operationType = params.opertationType
        this.positionType = params.positionType
        this.optionalSyncfields = params.optionalSyncfields
        this.onAddOptionalSyncfieldClick = params.onAddOptionalSyncfieldClick
        this.onRemoveOptionalSyncfieldClick = params.onRemoveOptionalSyncfieldClick

        this.derived.Start = ko.pureComputed(() => {
            return this.momentMetaStart()
                ? convertToServerString(this.momentMetaStart()!)
                : null
        })

        this.derived.Stop = this.derived.ExpiryDate = ko.pureComputed(() => {
            return this.momentMetaEnd()
                ? convertToServerString(this.momentMetaEnd()!)
                : null
        })

        this.derived.Department = ko.pureComputed(() => this.getDepartmentSyncfield('Department'))
        this.derived.DepartmentNumber = ko.pureComputed(() => this.getDepartmentSyncfield('OrgUnitnr'))
        this.derived.ExpiryDate = ko.pureComputed(() => this.momentMetaEnd() ? convertToServerString(this.momentMetaEnd()!) : null)
        this.derived.Manager = ko.pureComputed(() => this.getDepartmentSyncfield('Manager'))
        this.derived.Start = ko.pureComputed(() => this.momentMetaStart() ? convertToServerString(this.momentMetaStart()!) : null)
        this.derived.Stop = ko.pureComputed(() => this.momentMetaEnd() ? convertToServerString(this.momentMetaEnd()!) : null)
        this.derived.WwwUrl = ko.pureComputed(() => this.getWwwUrl())
    }

    private momentMetaStart(): moment.Moment | null {
        return !this.meta.isNoStartDate() && this.meta.start() ? moment(this.meta.start(), INPUT_DATE_FORMAT) : null
    }

    private momentMetaEnd(): moment.Moment | null {
        return !this.meta.isNoEndDate() && this.meta.end() ? moment(this.meta.end(), INPUT_DATE_FORMAT) : null
    }

    private getDepartmentSyncfield(key: TSyncfield_Department): string {
        const department = this.meta.department()
        return department
            ? getRawSyncFieldValue(key, department.syncfields) ?? ''
            : ''
    }

    private getWwwUrl() {
        let ret = ''
        const departments = this.dataset.departments
        if (this.meta.department() && departments?.length) {
            const getDepartment = (id: string) => {
                return departments.find(o => getRawSyncFieldValue('DepartmentNumber', o.syncfields) == id)
            }
            const graph: TObjectData_basic[] = []
            let department: TObjectData_basic | null | undefined = this.meta.department()
            while (department) {
                graph.push(department)
                const parentDepartmentNumber = getRawSyncFieldValue('ParentDepartmentNumber', department.syncfields)
                if (!parentDepartmentNumber) {
                    break
                }
                department = getDepartment(parentDepartmentNumber)
                if (!department || graph.includes(department)) {
                    break
                }
            }
            const syncfieldNames: TSyncfield[] = ['OrgUnitnr', 'Department']
            ret = graph.reverse().map(o => {
                const ret = syncfieldNames.map(k => getRawSyncFieldValue(k, o.syncfields))
                return ret.join(' ')
            }).join('\\')
        }
        return ret
    }

    public initErrorObjects() {
        this.errorManager.addErrorObject(this.eo.DepartmentNumber)
        this.errorManager.addErrorObject(this.eo.ExpiryDate)
        this.errorManager.addErrorObject(this.eo.Start)
        this.errorManager.addErrorObject(this.eo.Title)

        this.eo.DepartmentNumber.addValidator({
            fn: () => !!this.meta.department(),
            message: '',
        })

        this.eo.Title.addValidator({
            fn: () => !!this.attr.Title?.(),
            message: '',
        })

        const isPeriodValid = (): boolean => {
            const start = this.momentMetaStart()
            const end = this.momentMetaEnd()
            if (!start || !end) {
                return false;
            }
            return end.diff(start) > 0
        }

        this.eo.Start.addValidator({
            fn: () => !!this.momentMetaStart(),
            message: '',
            prerequisite: () => !this.meta.isNoStartDate(),
        })
        this.eo.ExpiryDate.addValidator({
            fn: () => !!this.momentMetaEnd(),
            message: '',
            prerequisite: () => !this.meta.isNoEndDate(),
        })

        this.eo.ExpiryDate.addValidator({
            fn: () => {
                const endMoment = this.momentMetaEnd()
                return !!endMoment && endMoment.diff(moment().startOf('day'), 'days') >= 1
            },
            message: () => `x must be in the future|x:${this.srcTrans('ExpiryDate')}`,
            prerequisite: () => !!this.momentMetaEnd() && !this.meta.isNoEndDate() && this.operationType == 'create',
        })

        this.eo.Start.addValidator({
            fn: () => isPeriodValid(),
            message: () => `x must be before y|x:${this.srcTrans('Start')}|y:${this.srcTrans('ExpiryDate')}`,
            prerequisite: () => !!this.momentMetaEnd() && !this.meta.isNoStartDate() && !this.meta.isNoEndDate(),
        })
        this.eo.ExpiryDate.addValidator({
            fn: () => isPeriodValid(),
            message: () => `x must be after y|x:${this.srcTrans('ExpiryDate')}|y:${this.srcTrans('Start')}`,
            prerequisite: () => !!this.momentMetaStart() && !this.meta.isNoStartDate() && !this.meta.isNoEndDate(),
        })
    }

    public getOptionalSyncfield(syncfield: ISyncField): KnockoutObservable<any> | undefined {
        return this.optionalSyncfieldObservables[syncfield.name]
    }

    dispose() {
        this.kosub.unsuscribeAll()
    }
}
